import React from 'react';
import { Feature } from 'ol';
import { unByKey } from 'ol/Observable';
import VectorLayer from 'ol/layer/Vector';
import { LineString, Point } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import { useParams } from 'react-router-dom';
import { Fill, Icon, Style, Text as TextFeature } from 'ol/style';

import { MapContext } from '../components/map/map-context';
import { GameDocumentContext } from '../contexts/game-document';
import { GetTasksByIds, GetTasksByZoneId } from '../utils/game-document';
import { MapIllustrationContext } from '../components/map/map-illustration-context';

export const useGameRoute = () => {
  const { zoneId } = useParams();

  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);
  const [state] = React.useContext(GameDocumentContext);

  const annotationSourceRef = React.useRef<VectorSource | null>(null);
  const [vectorRouteLayer, setVectorRouteLayer] =
    React.useState<VectorLayer<VectorSource> | null>(null);

  const mapAssId = React.useMemo(() => {
    if (!state.gameDocument) return '';
    if (!zoneId) return state.gameDocument.rules.worldMap.mapAssId;

    const zone = state.gameDocument.rules.worldMap.zones.find(
      (z) => z.zoneAssId === zoneId
    );
    if (!zone) return '';
    return zone.mapAssId ?? '';
  }, [state.gameDocument, zoneId]);

  const tasks = React.useMemo(() => {
    if (!state.gameDocument) return [];

    if (zoneId) return GetTasksByZoneId(zoneId, state.gameDocument);

    const taskIds = state.gameDocument?.rules?.worldMap?.tasks?.map(
      (task) => task.taskAssId
    );

    return GetTasksByIds(state.gameDocument, taskIds);
  }, [state.gameDocument, zoneId]);

  const routes = React.useMemo(() => {
    if (!state.gameDocument) return [];

    const allRoutes = state?.gameDocument?.assets?.routes ?? [];
    return allRoutes.filter((item) => item.mapId === mapAssId);
  }, [state.gameDocument, mapAssId]);

  const visibleRoutes = React.useMemo(
    () => routes.filter((route) => route.isVisible !== false),
    [routes]
  );

  const mapEntity = React.useMemo(() => {
    if (!state.gameDocument) return;
    return (state.gameDocument.assets.maps ?? []).find(
      (m) => m.id === mapAssId
    );
  }, [state.gameDocument, mapAssId]);

  const mapContext = React.useMemo(
    () => (mapEntity?.type === 'illustrated' ? mapIllustration : map),
    [mapEntity?.type, map, mapIllustration]
  );

  React.useEffect(() => {
    if (!mapContext) {
      setVectorRouteLayer(null);
      return;
    }

    const layersCollection = mapContext.getLayers();

    const tryResolve = () => {
      const existingLayer = layersCollection
        .getArray()
        .find((l) => l.get('id') === 'vectorRoute') as
        | VectorLayer<VectorSource>
        | undefined;

      setVectorRouteLayer((prev) =>
        existingLayer && existingLayer !== prev ? existingLayer : prev
      );
    };

    tryResolve();
    const key = layersCollection.on('add', tryResolve);

    return () => {
      unByKey(key);
    };
  }, [mapContext, mapAssId, zoneId]);

  const buildRouteFeatures = React.useCallback(
    (id: string, startCoordinate: number[], endCoordinate: number[]) => {
      const lineName = `Line between task-${id}`;
      const points = [startCoordinate, endCoordinate];

      const lineFeature = new Feature({
        geometry: new LineString(points),
        name: lineName
      });

      const midPoint = [
        (startCoordinate[0] + endCoordinate[0]) / 2,
        (startCoordinate[1] + endCoordinate[1]) / 2
      ];

      let rotation = Math.atan2(
        endCoordinate[1] - startCoordinate[1],
        endCoordinate[0] - startCoordinate[0]
      );
      rotation = (rotation - Math.PI / 2) * -1;

      const arrowFeature = new Feature(new Point(midPoint));
      arrowFeature.setStyle(
        new Style({
          image: new Icon({
            src: '/navigation.png',
            scale: 0.07,
            rotateWithView: true,
            rotation
          })
        })
      );

      const titleResId =
        visibleRoutes.find((x) => x.id === id)?.titleResId ?? '';
      const titleFeature = new Feature({ geometry: new Point(midPoint) });
      titleFeature.setStyle(
        new Style({
          text: new TextFeature({
            text:
              state.gameDocument?.resources.find((r) => r.id === titleResId)
                ?.value ?? '',
            offsetY: -20,
            font: 'bold 18px Calibri, sans-serif',
            fill: new Fill({ color: 'black' })
          })
        }) as any
      );

      return [lineFeature, arrowFeature, titleFeature];
    },
    [visibleRoutes, state.gameDocument]
  );

  React.useEffect(() => {
    if (!vectorRouteLayer) {
      annotationSourceRef.current = null;
      return;
    }

    const source = vectorRouteLayer.getSource() ?? null;
    annotationSourceRef.current = source;
    source?.clear();

    return () => {
      annotationSourceRef.current?.clear();
    };
  }, [vectorRouteLayer, mapAssId, zoneId]);

  React.useEffect(() => {
    const source = annotationSourceRef.current;
    if (!source) return;

    source.clear();

    if (visibleRoutes.length === 0) return;

    const taskById = new Map(tasks.map((t) => [t.id, t]));

    for (const route of visibleRoutes) {
      const startCoordinate =
        taskById.get(route.fromTaskId)?.boundary?.geometry?.coordinates ?? [];
      const endCoordinate =
        taskById.get(route.toTaskId)?.boundary?.geometry?.coordinates ?? [];

      if (startCoordinate.length > 0 && endCoordinate.length > 0) {
        source.addFeatures(
          buildRouteFeatures(route.id, startCoordinate, endCoordinate)
        );
      }
    }
  }, [vectorRouteLayer, mapAssId, visibleRoutes, tasks, buildRouteFeatures]);

  const removeAllAnnotationFeatures = React.useCallback(() => {
    annotationSourceRef.current?.clear();
  }, []);

  return { removeAllAnnotationFeatures };
};
